"use client";

import React from "react";
import { cn } from "@/utilities/core/cn";

export interface PageNavigationOption<T extends string = string> {
    /** Unique identifier for the option */
    id: T;
    /** Display label for the option */
    label: string;
}

export interface PageNavigationProps<T extends string = string> {
    /** Array of navigation options */
    options: PageNavigationOption<T>[];
    /** Currently active option id */
    activeId: T;
    /** Callback when option changes */
    onChange: (id: T) => void;
    /** Whether the navigation should be sticky at the top */
    sticky?: boolean;
    /** Optional additional className for the container */
    className?: string;
}

/**
 * PageNavigation - A reusable page navigation component for switching between tabs/views
 *
 * @example
 * ```tsx
 * <PageNavigation
 *   options={[
 *     { id: "overview", label: "Overview" },
 *     { id: "matches", label: "Matches" },
 *   ]}
 *   activeId={activeTab}
 *   onChange={setActiveTab}
 *   sticky
 * />
 * ```
 */
export function PageNavigation<T extends string = string>({
    options,
    activeId,
    onChange,
    sticky = false,
    className,
}: PageNavigationProps<T>) {
    return (
        <div className={cn("py-0.5 tab-bar-surface", sticky && "sticky top-below-nav z-10", className)}>
            <div className="max-w-350 mx-auto px-4">
                <div className="flex gap-2 my-2 overflow-x-auto scrollbar-hide">
                    {options.map((option) => (
                        <button
                            key={option.id}
                            onClick={() => onChange(option.id)}
                            className={cn(
                                "px-5 py-3 font-medium text-xs uppercase cursor-pointer whitespace-nowrap",
                                activeId === option.id ? "btn-primary" : "link-themed"
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
